/**
 * Tasks API handler
 */

import type { RouteContext, ApiConfig } from '../types';
import type { VibecodeConfig } from '../../config/types';
import { safeDependencyLoad } from '../../templates/utils/fallback-handlers';

export async function tasksHandler(context: RouteContext): Promise<any> {
  const { request, config, requestId } = context;
  const method = request.method;

  if (method === 'GET') {
    return await handleTasksGet(context);
  }

  if (method === 'POST') {
    return await handleTasksPost(context);
  }

  throw new Error(`Method ${method} not supported for tasks endpoint`);
}

async function handleTasksGet(context: RouteContext): Promise<any> {
  const { request, config } = context;
  const url = new URL(request.url);
  const searchParams = url.searchParams;

  // Parse query parameters
  const queryParams = {
    type: searchParams.get('type') || 'all',
    status: searchParams.get('status') || 'pending',
    priority: searchParams.get('priority') || 'all',
    path: searchParams.get('path'),
    search: searchParams.get('search'),
    limit: Math.min(parseInt(searchParams.get('limit') || '100'), 1000),
    offset: Math.max(parseInt(searchParams.get('offset') || '0'), 0),
    sortBy: searchParams.get('sortBy') || 'priority',
    sortOrder: searchParams.get('sortOrder') || 'desc',
  };

  try {
    // Load dependencies with fallbacks
    const FileScanner = await safeDependencyLoad(
      async () => {
        const { FileScanner } = await import('../../server/index');
        return FileScanner;
      },
      () => createMockFileScanner() as any,
      'FileScanner'
    );

    const TaskExtractor = await safeDependencyLoad(
      async () => {
        const { TaskExtractor } = await import('../../server/index');
        return TaskExtractor;
      },
      () => createMockTaskExtractor() as any,
      'TaskExtractor'
    );

    // Initialize services
    const scanner = new FileScanner(config as VibecodeConfig);
    const extractor = new TaskExtractor();

    // Scan for files
    const files = await scanner.scan(queryParams.path || undefined);
    
    // Extract tasks from files
    const allTasks = [];
    for (const file of files) {
      try {
        const content = await scanner.readFile(file);
        const fileTasks = await extractor.extract(content, file);
        allTasks.push(...fileTasks);
      } catch (error) {
        console.warn(`Failed to extract tasks from ${file}:`, error);
      }
    }

    // Filter and sort tasks
    const filteredTasks = filterTasks(allTasks, queryParams);
    const sortedTasks = sortTasks(filteredTasks, queryParams.sortBy, queryParams.sortOrder);
    
    // Paginate
    const paginatedTasks = sortedTasks.slice(
      queryParams.offset,
      queryParams.offset + queryParams.limit
    );

    return {
      tasks: paginatedTasks,
      pagination: {
        total: sortedTasks.length,
        limit: queryParams.limit,
        offset: queryParams.offset,
        hasMore: queryParams.offset + queryParams.limit < sortedTasks.length,
      },
      stats: {
        total: allTasks.length,
        filtered: filteredTasks.length,
        byType: getTaskStats(filteredTasks),
      },
      query: queryParams,
    };

  } catch (error) {
    console.error('Tasks scan error:', error);
    throw new Error(`Failed to scan tasks: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

async function handleTasksPost(context: RouteContext): Promise<any> {
  const { request } = context;
  
  try {
    const body = await request.json();
    const { taskId, updates } = body;

    if (!taskId || !updates) {
      throw new Error('taskId and updates are required');
    }

    // In a real implementation, you would:
    // 1. Find the task by ID
    // 2. Update the task in the source file
    // 3. Return the updated task

    return {
      success: true,
      taskId,
      updates,
      message: 'Task update received (implementation pending)',
    };

  } catch (error) {
    throw new Error(`Failed to update task: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Mock implementations for when real services aren't available
function createMockFileScanner() {
  return class MockFileScanner {
    constructor(config: any) {}
    
    async scan(path?: string) {
      return [];
    }
    
    async readFile(path: string) {
      throw new Error('File scanner not available');
    }
  };
}

function createMockTaskExtractor() {
  return class MockTaskExtractor {
    async extract(content: string, filePath: string) {
      // Basic regex-based task extraction
      const tasks = [];
      const lines = content.split('\n');
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const match = line.match(/(?:\/\/|\/\*|<!--)\s*(TODO|FIXME|HACK|NOTE|BUG|OPTIMIZE|REFACTOR):?\s*(.+?)(?:\*\/|-->)?$/i);
        
        if (match) {
          tasks.push({
            id: `mock_${filePath}_${i}`,
            type: match[1].toLowerCase(),
            content: match[2].trim(),
            file: filePath,
            line: i + 1,
            priority: 'medium',
            phase: 'Backlog',
            milestone: 'Unassigned',
            tags: [],
            created: new Date().toISOString(),
          });
        }
      }
      
      return tasks;
    }
  };
}

function filterTasks(tasks: any[], params: any) {
  return tasks.filter(task => {
    if (params.type !== 'all' && task.type !== params.type) {
      return false;
    }
    
    if (params.status !== 'all' && task.status !== params.status) {
      return false;
    }
    
    if (params.priority !== 'all' && task.priority !== params.priority) {
      return false;
    }
    
    if (params.search && !task.content.toLowerCase().includes(params.search.toLowerCase())) {
      return false;
    }
    
    return true;
  });
}

function sortTasks(tasks: any[], sortBy: string, sortOrder: string) {
  return tasks.sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'priority':
        const priorityOrder: Record<string, number> = { high: 3, medium: 2, low: 1 };
        comparison = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
        break;
      case 'date':
        comparison = new Date(a.created).getTime() - new Date(b.created).getTime();
        break;
      case 'type':
        comparison = a.type.localeCompare(b.type);
        break;
      case 'path':
        comparison = a.file.localeCompare(b.file);
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'desc' ? -comparison : comparison;
  });
}

function getTaskStats(tasks: any[]) {
  const stats: Record<string, number> = {};
  
  for (const task of tasks) {
    stats[task.type] = (stats[task.type] || 0) + 1;
  }
  
  return stats;
}